import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildConfigurableTooltipFormatter, buildConfigurableAxisLabel } from '../../utils/chart.utils';
import { buildSecondaryXAxisOption, buildSecondaryYAxisOption } from '../../utils/axis.utils';

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

/**
 * Step Line Chart.
 *
 * A line chart that connects points with horizontal/vertical segments instead
 * of a sloped line — useful for values that hold constant between samples
 * (inventory levels, price tiers, status over time, headcount, etc.).
 *
 * Modeled on {@link LineChartComponent}: same config/theme/filter/reflow wiring,
 * the only meaningful difference is `step: 'middle'` on the line series. The
 * step position can be overridden via `data.chartConfig.step`
 * ('start' | 'middle' | 'end').
 *
 * The legacy `CountComponent` name is re-exported as an alias so existing
 * renderer registrations (`case 'count'`) keep working.
 */
@Component({
  selector: 'app-step-line-chart',
  standalone: true,
  imports: [CapitalCasePipe],
  templateUrl: './count.component.html',
  styleUrl: './count.component.scss',
})
export class StepLineChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  protected myChart!: echarts.ECharts;
  protected allCategories: string[] = [];

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private lastChartData?: { category: string[]; value: number[] };

  /** ECharts `step` position; 'middle' by default, configurable via chartConfig.step. */
  private get stepPosition(): 'start' | 'middle' | 'end' {
    const raw = `${this.data?.chartConfig?.step ?? 'middle'}`.toLowerCase();
    return raw === 'start' || raw === 'end' ? raw : 'middle';
  }

  ngAfterViewInit(): void {
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => [],
      getCategories: () => this.allCategories,
    });

    this.loadAndRenderChart(initialFilters ?? undefined);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init is required because ECharts' registered 'dvm' theme
    // updates do NOT propagate to already-initialized chart instances.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastChartData || !this.myChart) return;
      const chartDom = document.getElementById(this.data?.widgetId);
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.renderChart(this.lastChartData);
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
    }

    this.myChart.showLoading();

    this.configService.getConfig(this.data.apiUrl, this.data, filters)?.subscribe({
      next: (res: any) => {
        if (!res) return;
        const dataKey = this.data?.dataKey;
        const raw = dataKey ? res?.[dataKey] : (res?.data || res);
        if (Array.isArray(raw)) {
          this.wrapper.setSourceData(raw, this.data?.categoryKey || 'category');
        }
        let chartData: any;
        if (Array.isArray(raw)) {
          const catKey = this.data?.categoryKey || 'category';
          const valKey = this.data?.valueKey || 'value';
          chartData = {
            category: raw.map((item: any) => item[catKey]),
            value: raw.map((item: any) => item[valKey]),
          };
        } else {
          chartData = raw;
        }
        this.renderChart(chartData);
        this.addResizeListener();
      },
      error: () => {
        this.myChart?.showLoading();
      },
    });
  }

  protected renderChart(chartData?: { category: string[]; value: number[] }): void {
    if (chartData) this.lastChartData = chartData;
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    this.allCategories = chartData?.category || [];

    const tooltipCfg = this.data?.chartConfig?.tooltip;
    const formatter = buildConfigurableTooltipFormatter(tooltipCfg);
    const showTooltip = tooltipCfg?.show !== false;
    const labelsAllowed = this.data?.chartConfig?.showLabels !== false;
    const showDataLabels = labelsAllowed && (
      this.data?.chartConfig?.showDataLabels === true ||
      this.data?.chartConfig?.showLineLabels === true
    );

    const categories = chartData?.category || [];
    const enableDoubleXAxis = this.data?.enableDoubleXAxis === true;
    const enableDoubleYAxis = this.data?.enableDoubleYAxis === true;

    const primaryXAxis: any = {
      type: 'category',
      data: categories,
      triggerEvent: true,
      axisLabel: { show: labelsAllowed },
    };
    const secondaryX = buildSecondaryXAxisOption(this.data, categories);
    if (secondaryX && !labelsAllowed) {
      secondaryX.axis.axisLabel = { ...(secondaryX.axis.axisLabel || {}), show: false };
    }
    const xAxisOption = secondaryX ? [primaryXAxis, secondaryX.axis] : primaryXAxis;

    const yAxisLabel = buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis);
    const primaryYAxis: any = {
      type: 'value',
      triggerEvent: true,
      axisLabel: {
        ...(yAxisLabel || {}),
        show: labelsAllowed && yAxisLabel?.show !== false,
      },
    };
    const secondaryY = buildSecondaryYAxisOption(this.data);
    if (secondaryY && !labelsAllowed) {
      secondaryY.axisLabel = { ...(secondaryY.axisLabel || {}), show: false };
    }
    const yAxisOption = secondaryY ? [primaryYAxis, secondaryY] : primaryYAxis;

    const option = {
      tooltip: {
        show: showTooltip,
        trigger: tooltipCfg?.trigger || 'axis',
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        ...(formatter ? { formatter } : {}),
      },
      grid: {
        left: '3%',
        right: enableDoubleYAxis ? '8%' : '4%',
        top: '5%',
        bottom: enableDoubleXAxis ? '12%' : '3%',
        containLabel: true,
      },
      xAxis: xAxisOption,
      yAxis: yAxisOption,
      series: [
        {
          type: 'line',
          step: this.stepPosition,
          data: chartData?.value || [],
          lineStyle: { width: 2 },
          itemStyle: { color: theme.chart?.palette?.[1] || '#91cc75' },
          label: {
            show: showDataLabels,
            position: 'top',
          },
        },
      ],
    };

    applyRoleVisibilityToOption(option, this.data);

    this.myChart.setOption(option, true);
    setTimeout(() => this.myChart.hideLoading(), 1000);

    this.wrapper.afterRender();
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}

/**
 * Backward-compatible alias. The chart renderers register this component under
 * `case 'count'`; keeping the old export name avoids touching those call sites
 * while the modern `StepLineChartComponent` name is used going forward.
 */
export { StepLineChartComponent as CountComponent };
